<template>
    <WalletWithdrawalModal v-if="walletState.wallet && showWalletWithdrawalModal" v-model="showWalletWithdrawalModal" />
    <WalletDepositModal v-if="walletState.wallet && showWalletDepositModal" v-model="showWalletDepositModal" />
    <div class="flex h-auto w-full flex-col items-start justify-start py-8">
        <div class="flex h-auto w-full flex-col items-start justify-start">
            <h1 class="text-4xl font-bold text-primary-500">{{ $t('wallet.my_wallets') }}</h1>
            <p class="text-sm font-medium text-gray-500">{{ $t('wallet.my_wallets_subtitle') }}</p>
        </div>
        <div
            class="flex h-auto w-full flex-col items-start justify-start py-6 md:space-x-0 md:space-y-6 lg:flex-row lg:space-x-6 lg:space-y-0">
            <div class="flex h-auto w-full flex-col items-start justify-start space-y-6 lg:w-2/3">
                <div class="grid h-auto w-full grid-cols-1 gap-4 lg:grid-cols-2">
                    <div
                        v-for="wallet in wallets"
                        :key="wallet.id"
                        class="relative col-span-1 flex h-44 cursor-pointer flex-col items-start justify-start rounded-lg border border-gray-200 bg-slate-100 shadow-md transition-shadow duration-300 hover:shadow-lg"
                        @click.prevent="handleFilteringTransactions(wallet)">
                        <CheckIcon
                            v-if="currentlySelectedWallet === wallet.type"
                            class="absolute -right-3 -top-3 h-6 w-6 text-green-500" />
                        <div class="flex h-3/5 w-full items-center justify-between space-x-2">
                            <div class="flex h-full w-2/3 flex-col items-start justify-start p-4">
                                <p class="text-xl font-semibold text-gray-900">
                                    {{ $t(`wallet.${wallet.type}`) }}
                                </p>
                                <p v-if="wallet.type === 'secupay'" class="text-sm text-gray-500">
                                    {{ $t('wallet.secupay_amount_info_text') }}
                                </p>
                                <p v-else class="text-lg font-medium text-gray-900">
                                    {{ formatCurrency(wallet.balance, wallet.currency) }}
                                </p>
                            </div>
                            <div class="h-full w-1/3 px-4 py-3">
                                <div
                                    class="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-white">
                                    <img :src="wallet.logo_url" alt="Wallet Icon" class="h-full w-full object-cover" />
                                </div>
                            </div>
                        </div>
                        <div class="flex h-2/5 w-full items-center justify-start space-x-2 px-4">
                            <button
                                v-if="wallet.can_deposit"
                                class="flex items-center justify-center space-x-2 rounded-md bg-yellow-200 px-4 py-2 text-sm text-gray-900 duration-150 hover:bg-yellow-300"
                                @click.stop="handleOpenWalletDepositModal(wallet)">
                                {{ $t('wallet.deposit') }}
                                <DepositIcon class="ml-2 h-2 w-2" />
                            </button>
                            <button
                                v-if="wallet.can_withdraw"
                                class="flex items-center justify-center space-x-2 rounded-md bg-green-200 px-4 py-2 text-sm text-gray-900 duration-150 hover:bg-green-300"
                                @click.stop="handleOpenWalletWithDrawalModal(wallet)">
                                {{ $t('wallet.withdraw') }}
                                <DepositIcon class="ml-2 h-2 w-2 rotate-180" />
                            </button>
                        </div>
                    </div>
                </div>
                <div
                    class="flex h-auto w-full flex-col items-start justify-start space-y-4 rounded-lg bg-white p-4 shadow">
                    <WalletTransactionsTable
                        v-if="transactions.length"
                        id="wallet-transactions-table"
                        :transactions="currentlySelectedWallet ? filteredTransactions : transactions"
                        class="h-full w-full"
                        @update:currentPage="handlePageChange" />
                    <div v-else class="flex h-auto w-full flex-col items-center justify-center space-y-2 py-8">
                        <TransactionIcon class="h-10 w-10 text-gray-500" />
                        <p class="text-lg text-gray-500">{{ $t('wallet.no_transactions') }}</p>
                    </div>
                </div>
            </div>
            <div class="h-auto w-full lg:w-1/3">
                <AccountSidePanel />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import AccountSidePanel from './components/account/AccountSidePanel.vue'
import DepositIcon from './components/base/icons/DepositIcon.vue'
import { useWalletState, WalletState, Wallet } from './utilities/account/useWallet'
import WalletTransactionsTable from './components/account/wallet/WalletTransactionsTable.vue'
import { storeToRefs } from 'pinia'
import { formatCurrency } from './utilities/helpers'
import { ref } from 'vue'
import WalletWithdrawalModal from './components/account/wallet/WalletWithdrawalModal.vue'
import CheckIcon from './components/base/icons/CheckIcon.vue'
import TransactionIcon from './components/base/icons/TransactionIcon.vue'
import WalletDepositModal from './components/account/wallet/WalletDepositModal.vue'

const walletState = useWalletState() as WalletState
const currentlySelectedWallet = ref<string | null>(null)
const { wallets, transactions, filteredTransactions } = storeToRefs(walletState)

const showWalletWithdrawalModal = ref(false)
const showWalletDepositModal = ref(false)

const handleOpenWalletWithDrawalModal = (wallet: Wallet) => {
    walletState.setWallet(wallet)
    showWalletWithdrawalModal.value = true
}

const handleOpenWalletDepositModal = (wallet: Wallet) => {
    walletState.setWallet(wallet)
    showWalletDepositModal.value = true
    console.log('open wallet deposit modal')
}

const handlePageChange = () => {
    window.scrollTo({ top: document.getElementById('wallet-transactions-table')?.offsetTop, behavior: 'smooth' })
}

const handleFilteringTransactions = (wallet: Wallet) => {
    currentlySelectedWallet.value === wallet.type
        ? (currentlySelectedWallet.value = null)
        : (currentlySelectedWallet.value = wallet.type)
    walletState.setFilteredTransactions(wallet.type)
}

onMounted(async () => {
    await walletState.fetchWalletData()
})
</script>
